/**
 * @file Ecran En ligne : classements, amis (rejoindre leur salon), courrier (pieces jointes), evenements + boutique d'evenement, pseudo.
 * Toutes les donnees viennent du serveur (ctx.online) ; hors ligne l'ecran l'explique et propose de reessayer.
 */
import { stageValueToDisplay } from '@core/index';
import { formatBig, formatDuration } from '@shared/format';
import { Component } from '../core/Component';
import { clear, h, setText, toggleClass } from '../core/dom';
import { closeScreen, notify } from '../core/feedback';
import { uiIcon } from '../core/uiIcons';
import { makeTabs, sheetHeader } from '../core/widgets';
import type { TabsHandle } from '../core/widgets';
import type { UiBoard, UiContext, UiEvent, UiFriend, UiMail, UiOffer, UiOnlinePort, UiResult } from '../UiContext';

type TabId = 'boards' | 'friends' | 'mail' | 'events';
const TAB_IDS: readonly TabId[] = ['boards', 'friends', 'mail', 'events'];

export class OnlineScreen extends Component {
  private tabs!: TabsHandle<TabId>;
  private readonly panes = new Map<TabId, HTMLElement>();
  private readonly seq = new Map<TabId, number>();
  private banner!: HTMLElement;
  private nick!: HTMLInputElement;
  private nickBtn!: HTMLButtonElement;
  private body!: HTMLElement;
  private lastStatus = '';

  constructor(ctx: UiContext) {
    super(ctx, 'div', 'screen online');
  }

  private get api(): UiOnlinePort | undefined {
    return this.ctx.online;
  }

  protected build(): void {
    const t = this.ctx.i18n;
    this.el.setAttribute('data-ui-block', '');

    this.nick = h('input', { class: 'text-input', type: 'text', maxlength: 24, autocomplete: 'off', autocapitalize: 'off', spellcheck: false, placeholder: t.t('online.nick.label') });
    this.nick.value = this.api?.username ?? this.ctx.sim.data.meta.nickname;
    this.nickBtn = h('button', { class: 'btn small primary', type: 'button', 'data-ui-block': true, onclick: () => void this.saveNick() }, t.t('online.nick.save'));
    const nickRow = h('div', { class: 'online-nick' }, h('label', { class: 'online-nick-label' }, t.t('online.nick.label')), this.nick, this.nickBtn);

    this.banner = h('div', { class: 'online-banner panel is-hidden' }, h('span', {}, t.t('online.offline')), h('button', { class: 'btn small', type: 'button', 'data-ui-block': true, onclick: () => void this.retry() }, t.t('online.retry')));

    this.tabs = makeTabs<TabId>(
      [
        { id: 'boards', label: t.t('online.tab.boards') },
        { id: 'friends', label: t.t('online.tab.friends') },
        { id: 'mail', label: t.t('online.tab.mail') },
        { id: 'events', label: t.t('online.tab.events') },
      ],
      'boards',
      (id) => void this.load(id),
    );
    this.body = h('div', { class: 'sheet-body' });
    for (const id of TAB_IDS) {
      const pane = h('div', { class: `pane pane-${id}` });
      this.panes.set(id, pane);
      this.body.append(pane);
    }
    this.el.append(sheetHeader(this.ctx, t.t('online.title')), nickRow, this.banner, this.tabs.el, this.body);
    this.syncPanes();
    void this.load('boards');
    // une reconnexion (ou une perte) rafraichit l'ecran
    if (this.api) this.addDisposer(this.api.onStatus(() => this.onStatus()));
  }

  private onStatus(): void {
    const s = this.api?.status ?? 'disabled';
    if (s === this.lastStatus) return;
    this.lastStatus = s;
    this.syncPanes();
    if (s === 'online') void this.load(this.tabs.active());
  }

  private syncPanes(): void {
    const active = this.tabs.active();
    for (const id of TAB_IDS) toggleClass(this.panes.get(id) as HTMLElement, 'active', id === active);
    toggleClass(this.banner, 'is-hidden', this.api?.status === 'online');
  }

  // ------------------------------------------------------------------------------------------------ utilitaires

  private errText(code: string): string {
    const key = `online.err.${code}`;
    const t = this.ctx.i18n.tDyn(key);
    return t === key ? this.ctx.i18n.t('online.err.generic') : t;
  }

  private fail(code: string): void {
    notify(this.errText(code), 'warning');
  }

  private pane(id: TabId): HTMLElement {
    return this.panes.get(id) as HTMLElement;
  }

  private note(text: string, cls = 'hint'): HTMLElement {
    return h('p', { class: cls }, text);
  }

  private async retry(): Promise<void> {
    await this.api?.retry();
    this.syncPanes();
    void this.load(this.tabs.active());
  }

  private async saveNick(): Promise<void> {
    const api = this.api;
    if (!api) return;
    const name = this.nick.value.trim();
    if (!/^[A-Za-z0-9_-]{3,24}$/.test(name)) {
      notify(this.ctx.i18n.t('online.nick.hint'), 'warning');
      return;
    }
    this.nickBtn.disabled = true;
    const r = await api.setNickname(name);
    this.nickBtn.disabled = false;
    if (r.ok) notify(this.ctx.i18n.t('online.nick.saved'), 'success');
    else this.fail(r.error);
  }

  /** (Re)charge un onglet ; ignore une reponse devenue obsolete (onglet change / recharge entre-temps). */
  private async load(id: TabId): Promise<void> {
    this.syncPanes();
    const pane = this.pane(id);
    const api = this.api;
    if (!api) return;
    if (api.status !== 'online') {
      clear(pane);
      return;
    }
    const n = (this.seq.get(id) ?? 0) + 1;
    this.seq.set(id, n);
    if (pane.childElementCount === 0) pane.append(this.note(this.ctx.i18n.t('online.loading')));
    const stale = (): boolean => this.seq.get(id) !== n;
    switch (id) {
      case 'boards': {
        const r = await api.leaderboards();
        if (!stale()) this.fill(pane, r, (v) => this.renderBoards(v));
        break;
      }
      case 'friends': {
        const r = await api.friends();
        if (!stale()) this.fill(pane, r, (v) => this.renderFriends(v));
        break;
      }
      case 'mail': {
        const r = await api.inbox();
        if (!stale()) this.fill(pane, r, (v) => this.renderMail(v));
        break;
      }
      case 'events': {
        const [ev, shop] = await Promise.all([api.events(), api.shop()]);
        if (stale()) break;
        clear(pane);
        if (!ev.ok) pane.append(this.note(this.errText(ev.error)));
        else pane.append(...this.renderEvents(ev.value, shop.ok ? shop.value : null));
        break;
      }
    }
  }

  private fill<T>(pane: HTMLElement, r: UiResult<T>, render: (v: T) => HTMLElement[]): void {
    clear(pane);
    if (!r.ok) {
      pane.append(this.note(this.errText(r.error)), h('button', { class: 'btn small', type: 'button', 'data-ui-block': true, onclick: () => void this.load(this.tabs.active()) }, this.ctx.i18n.t('online.refresh')));
      return;
    }
    pane.append(...render(r.value));
  }

  // ------------------------------------------------------------------------------------------------ classements

  private formatScore(b: UiBoard, score: number): string {
    if (b.kind === 'stage') {
      const d = stageValueToDisplay(this.ctx.config.balance, score);
      return `${d.stage}-${d.level}`;
    }
    return formatBig(score);
  }

  private renderBoards(boards: UiBoard[]): HTMLElement[] {
    const t = this.ctx.i18n;
    return boards.map((b) => {
      const key = `online.board.${b.id}`;
      const title = t.tDyn(key) === key ? b.name : t.tDyn(key);
      const head = h('div', { class: 'board-head' }, h('b', {}, title));
      if (b.resetsAt) head.append(h('span', { class: 'hint' }, t.t('online.board.resets', { time: formatDuration((b.resetsAt - Date.now()) / 1000) })));
      const list = h('div', { class: 'board-rows' });
      if (b.rows.length === 0) list.append(this.note(t.t('online.board.noScore')));
      const row = (r: { rank: number; name: string; score: number; me: boolean }): HTMLElement =>
        h('div', { class: `board-row${r.me ? ' me' : ''}` }, h('span', { class: 'rk' }, `#${r.rank}`), h('span', { class: 'nm' }, r.me ? `${r.name} (${t.t('online.board.you')})` : r.name), h('b', { class: 'sc' }, this.formatScore(b, r.score)));
      for (const r of b.rows) list.append(row(r));
      if (b.me && !b.rows.some((r) => r.me)) list.append(h('div', { class: 'board-gap' }, '…'), row(b.me));
      return h('div', { class: 'panel board' }, head, list);
    });
  }

  // ------------------------------------------------------------------------------------------------ amis

  private renderFriends(all: UiFriend[]): HTMLElement[] {
    const t = this.ctx.i18n;
    const api = this.api as UiOnlinePort;
    const input = h('input', { class: 'text-input', type: 'text', maxlength: 24, autocomplete: 'off', autocapitalize: 'off', spellcheck: false, placeholder: t.t('online.friend.placeholder') });
    const add = h('button', { class: 'btn small primary', type: 'button', 'data-ui-block': true, onclick: () => void this.addFriend(input) }, t.t('online.friend.addBtn'));
    const out: HTMLElement[] = [h('div', { class: 'panel friend-add' }, h('b', {}, t.t('online.friend.add')), h('div', { class: 'online-nick' }, input, add))];

    const act = (label: string, fn: () => Promise<UiResult>, cls = ''): HTMLElement =>
      h('button', {
        class: `btn small ${cls}`.trim(),
        type: 'button',
        'data-ui-block': true,
        onclick: () =>
          void fn().then((r) => {
            if (!r.ok) this.fail(r.error);
            void this.load('friends');
          }),
      }, label);
    const dot = (f: UiFriend): HTMLElement => h('i', { class: `dot${f.online ? ' on' : ''}`, title: f.online ? t.t('online.friend.on') : t.t('online.friend.off') });
    const section = (title: string, list: UiFriend[], buttons: (f: UiFriend) => HTMLElement[]): void => {
      if (list.length === 0) return;
      out.push(h('div', { class: 'panel' }, h('b', {}, title), ...list.map((f) => h('div', { class: 'friend-row' }, dot(f), h('span', { class: 'nm' }, f.name), ...buttons(f)))));
    };
    section(t.t('online.friend.requests'), all.filter((f) => f.state === 'incoming'), (f) => [act(t.t('online.friend.accept'), () => api.acceptFriend(f.id), 'primary'), act(t.t('online.friend.remove'), () => api.removeFriend(f.id))]);
    section(
      t.t('online.friend.list'),
      all.filter((f) => f.state === 'friend').sort((a, b) => Number(b.online) - Number(a.online) || a.name.localeCompare(b.name)),
      (f) => [
        ...(f.online && this.ctx.hub ? [h('button', { class: 'btn small primary', type: 'button', 'data-ui-block': true, onclick: () => (this.ctx.hub?.joinFriend(f.id), closeScreen()) }, t.t('online.friend.join'))] : []),
        act('✕', () => api.removeFriend(f.id)),
      ],
    );
    section(t.t('online.friend.pending'), all.filter((f) => f.state === 'outgoing'), (f) => [act(t.t('online.friend.remove'), () => api.removeFriend(f.id))]);
    if (all.length === 0) out.push(this.note(t.t('online.empty')));
    return out;
  }

  private async addFriend(input: HTMLInputElement): Promise<void> {
    const name = input.value.trim();
    if (name.length < 3) return;
    const r = await this.api?.addFriend(name);
    if (!r) return;
    if (!r.ok) {
      this.fail(r.error);
      return;
    }
    input.value = '';
    notify(this.ctx.i18n.t(r.value === 'friend' ? 'online.friend.added' : 'online.friend.sent'), 'success');
    void this.load('friends');
  }

  // ------------------------------------------------------------------------------------------------ courrier

  private renderMail(list: UiMail[]): HTMLElement[] {
    const t = this.ctx.i18n;
    const api = this.api as UiOnlinePort;
    if (list.length === 0) return [this.note(t.t('online.mail.empty'))];
    // les messages sans piece jointe sont lus des qu'ils sont affiches
    for (const m of list) if (!m.read && !m.claimable) void api.readMail(m.id);
    return list.map((m) => {
      const buttons: HTMLElement[] = [];
      if (m.claimable) buttons.push(h('button', { class: 'btn small primary', type: 'button', 'data-ui-block': true, onclick: () => void this.mailAction(api.claimMail(m.id)) }, t.t('online.mail.claim')));
      else buttons.push(h('button', { class: 'btn small', type: 'button', 'data-ui-block': true, onclick: () => void this.mailAction(api.deleteMail(m.id)) }, t.t('online.mail.delete')));
      return h(
        'div',
        { class: `panel mail${m.read ? '' : ' unread'}` },
        h('div', { class: 'mail-head' }, h('b', {}, m.title), h('span', { class: 'hint' }, t.t('online.mail.from', { name: m.from === 'system' ? 'Cat Gunner' : m.from }))),
        h('p', { class: 'mail-body' }, m.body),
        m.rewardText ? h('div', { class: 'mail-reward' }, m.rewardText) : null,
        h('div', { class: 'btn-row' }, ...buttons),
      );
    });
  }

  private async mailAction(p: Promise<UiResult>): Promise<void> {
    const r = await p;
    if (!r.ok) this.fail(r.error);
    void this.load('mail');
  }

  // ------------------------------------------------------------------------------------------------ evenements + boutique

  private renderEvents(events: UiEvent[], shop: { offers: UiOffer[]; catnip: number } | null): HTMLElement[] {
    const t = this.ctx.i18n;
    const api = this.api as UiOnlinePort;
    const out: HTMLElement[] = [];

    const offers = shop?.offers ?? [];
    const shopBox = h('div', { class: 'panel shop' }, h('div', { class: 'board-head' }, h('b', {}, uiIcon('bag'), ` ${t.t('online.shop.title')}`), h('span', { class: 'hint' }, t.t('online.shop.balance', { n: shop?.catnip ?? 0 }))));
    if (offers.length === 0) shopBox.append(this.note(t.t('online.shop.empty')));
    for (const o of offers) {
      const poor = (shop?.catnip ?? 0) < o.priceCatnip || o.remaining === 0;
      const buy = h('button', {
        class: `btn small primary${poor ? ' disabled' : ''}`,
        type: 'button',
        'data-ui-block': true,
        onclick: () =>
          void api.buy(o).then((r) => {
            if (!r.ok) this.fail(r.error);
            void this.load('events');
          }),
      }, `${o.priceCatnip} ${t.t('online.shop.buy')}`);
      shopBox.append(h('div', { class: 'offer-row' }, h('div', { class: 'offer-info' }, h('b', {}, o.name), h('span', { class: 'hint' }, o.grantsText + (o.remaining !== null ? `  ·  ${t.t('online.shop.left', { n: o.remaining })}` : ''))), buy));
    }
    out.push(shopBox);

    if (events.length === 0) out.push(this.note(t.t('online.empty')));
    const now = Date.now();
    for (const e of [...events].sort((a, b) => Number(b.active) - Number(a.active) || a.startsAt - b.startsAt)) {
      const status = e.active ? t.t('online.event.ends', { time: formatDuration((e.endsAt - now) / 1000) }) : t.t('online.event.starts', { time: formatDuration((e.startsAt - now) / 1000) });
      const card = h('div', { class: `panel event${e.active ? ' active' : ''}` }, h('div', { class: 'board-head' }, h('b', {}, e.name), h('span', { class: 'pill' }, e.active ? t.t('online.event.ongoing') : t.t('online.event.upcoming'))), this.note(e.description), h('div', { class: 'hint' }, status));
      if (e.multiplier) card.append(h('div', { class: 'tag' }, uiIcon('leaf'), ` ${t.t('online.event.multiplier', { n: e.multiplier })}`));
      if (e.goal) {
        const bar = h('div', { class: 'progress' });
        bar.style.setProperty('--progress', String(e.goal.target > 0 ? e.goal.progress / e.goal.target : 0));
        card.append(bar, h('div', { class: 'hint' }, t.t('online.event.progress', { n: e.goal.progress, max: e.goal.target })));
      }
      if (e.rewardText) card.append(h('div', { class: 'mail-reward' }, t.t('online.event.reward', { reward: e.rewardText })));
      if (e.claimed) card.append(h('div', { class: 'hint' }, `✓ ${t.t('online.event.claimed')}`));
      else if (e.claimable) card.append(h('button', { class: 'btn primary', type: 'button', 'data-ui-block': true, onclick: () => void api.claimEvent(e.id).then((r) => (r.ok ? undefined : this.fail(r.error))).then(() => this.load('events')) }, t.t('online.event.claim')));
      out.push(card);
    }
    return out;
  }

  override update(_dt: number): void {
    // rien a rafraichir en continu : l'ecran se recharge a l'ouverture d'un onglet, apres une action et quand la liaison change
    setText(this.nickBtn, this.ctx.i18n.t('online.nick.save'));
  }
}
